import Banner from '@frontend/components/banner/Banner'
import ImageGallery from '@frontend/components/image-gallery/ImageGallery'
import Slider from '@frontend/components/slider/Slider'
import VideoPlayer from '@frontend/components/video-player/VideoPlayer'
import BlogGrid from '@frontend/features/blog/gird/BlogGrid'
import ProductSlider from '@frontend/features/products/product-slider/ProductSlider'
import { Link } from '@nextui-org/react'
import parse, {
  attributesToProps,
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from 'html-react-parser'

export function fixInternalLinks(html_string: string) {
  const URLS = [
    'https://asavagroup-dev.srv-dev-01.home.ritthikrai.com',
    'https://asavagroup-uat.ritthikrai.com',
    'https://asavagroup-uat2.ritthikrai.com',
    'https://asavagroup-staging.ritthikrai.com',
    'https://asavagroup.com',
  ]

  return URLS.reduce(
    (result, url) => replaceUrlInHtml(result, url),
    html_string,
  )
}

function replaceUrlInHtml(html: string, url: string) {
  const pattern = new RegExp(
    `href="${url.replace(/\//g, '\\/')}\\/?p=(\\d+)"`,
    'g',
  )
  const replacement = 'data-internal-link="true" href="/blog/$1"'
  return html.replace(pattern, replacement)
}

const handleComponent = (domNode, Component, configKeys) => {
  const props = attributesToProps(domNode.attribs)
  const config = domNode.attribs['data-config']
  delete props['data-config']

  if (config) {
    const configProps = JSON.parse(config)
    const componentProps = configKeys.reduce(
      (props, key) => ({ ...props, [key]: configProps[key] }),
      {},
    )

    return (
      <div {...props}>
        <Component {...componentProps} />
      </div>
    )
  }
}

export function parseHtml(html: string) {
  const _content = html.replace(/\n{2,}/g, '')
  const content = fixInternalLinks(_content)

  const COMPONENTS = {
    'wp-block-asava-banner': {
      component: Banner,
      keys: [
        'VideoPlayerProps',
        'aspectRatios',
        'buttonText',
        'description',
        'images',
        'link',
        'subTitle',
        'title',
        'type',
      ],
    },
    'wp-block-asava-blog-grid': {
      component: BlogGrid,
      keys: ['limit', 'order', 'orderby'],
    },
    'wp-block-asava-image-gallery': {
      component: ImageGallery,
      keys: [
        'columns',
        'enableLightbox',
        'firstImageFullWidth',
        'galleryType',
        'gaps',
        'images',
        'mobileCarousel',
        'seeMoreLink',
        'showNavigation',
        'showPagination',
        'title',
      ],
    },
    'wp-block-asava-product-slider': {
      component: ProductSlider,
      keys: ['categories'],
    },
    'wp-block-asava-slider': { component: Slider, keys: ['items'] },
    'wp-block-asava-video-player': {
      component: VideoPlayer,
      keys: [
        'autoplay',
        'hideControls',
        'loop',
        'muted',
        'thumbnail',
        'videoType',
        'videoUrl',
        'vimeoId',
        'youtubeId',
      ],
    },
  }

  const options: HTMLReactParserOptions = {
    replace(domNode) {
      if (domNode instanceof Element && domNode.attribs) {
        const classList = domNode.attribs.class || ''

        const componentInfo = Object.entries(COMPONENTS).find(([className]) =>
          classList.includes(className),
        )

        if (componentInfo) {
          const [, { component, keys }] = componentInfo
          return handleComponent(domNode, component, keys)
        }

        // Convert internal links to Next.js Link components.
        const isInternalLink =
          domNode.name === 'a' &&
          domNode.attribs['data-internal-link'] === 'true'

        if (isInternalLink) {
          return (
            <Link href={domNode.attribs.href} {...domNode.attribs}>
              {domToReact(domNode[0].children, options)}
            </Link>
          )
        }
      }
    },
  }

  return parse(content, options)
}

export default parseHtml
